/**
 * Cleo ERP SaaS — Page d'inscription (cadrage §3.3)
 *
 * Champs : nom entreprise, pays, slug (auto-généré, modifiable, validation temps réel),
 * email, mot de passe, confirmation, CGU.
 * Après soumission : écran de confirmation.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Form, Input, Select, Button, Checkbox, Typography, Alert, Space, Spin,
} from 'antd';
import {
  BankOutlined, GlobalOutlined, LinkOutlined,
  MailOutlined, LockOutlined, CheckCircleFilled,
  CloseCircleFilled, LoadingOutlined,
} from '@ant-design/icons';
import api from '../../api/axios';

const { Title, Text, Paragraph } = Typography;

const RECAPTCHA_SITE_KEY = '6Leh5qEsAAAAAKeNyuzggnN3QdKCzOoeGZvySjmW';

// Slugification locale (même logique que le backend)
function slugify(name) {
  return name
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // supprimer accents
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[-\s]+/g, '-')
    .substring(0, 50)
    .replace(/^-+|-+$/g, '');
}

function RegisterPage() {
  const [form] = Form.useForm();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [resultSlug, setResultSlug] = useState('');

  // Slug validation temps réel
  const [slugValue, setSlugValue] = useState('');
  const [slugStatus, setSlugStatus] = useState(null); // 'checking' | 'available' | 'taken' | 'invalid'
  const [slugMessage, setSlugMessage] = useState('');
  const [slugManual, setSlugManual] = useState(false);

  // Charger la liste des pays
  useEffect(() => {
    api.get('/api/config/countries/')
      .then(({ data }) => setCountries(data))
      .catch(() => {});
  }, []);

  // Charger le script reCAPTCHA v3
  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  // Debounce check slug
  useEffect(() => {
    if (!slugValue || slugValue.length < 3) {
      setSlugStatus(null);
      setSlugMessage('');
      return;
    }

    setSlugStatus('checking');
    const timer = setTimeout(() => {
      api.get(`/api/tenants/check-slug/${slugValue}/`)
        .then(({ data }) => {
          setSlugStatus(data.available ? 'available' : 'taken');
          setSlugMessage(data.message);
        })
        .catch(() => {
          setSlugStatus('invalid');
          setSlugMessage('Erreur de vérification');
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [slugValue]);

  // Auto-générer le slug depuis le nom entreprise
  const handleCompanyNameChange = useCallback((e) => {
    if (!slugManual) {
      const generated = slugify(e.target.value);
      setSlugValue(generated);
      form.setFieldValue('slug', generated);
    }
  }, [slugManual, form]);

  // Slug modifié manuellement
  const handleSlugChange = (e) => {
    setSlugManual(true);
    const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSlugValue(val);
    form.setFieldValue('slug', val);
  };

  // Soumission
  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);

    try {
      // Obtenir le token reCAPTCHA v3
      let recaptcha_token = '';
      try {
        recaptcha_token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'register' });
      } catch (recaptchaErr) {
        setError('Erreur de vérification anti-spam. Rechargez la page.');
        setLoading(false);
        return;
      }

      const { data } = await api.post('/api/tenants/register/', {
        company_name: values.company_name,
        country_code: values.country_code,
        admin_email: values.admin_email,
        admin_password: values.admin_password,
        slug: slugValue,
        recaptcha_token,
      });
      setResultSlug(data.slug);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data;
      if (typeof detail === 'object') {
        // Erreurs de validation par champ
        const messages = Object.values(detail).flat().join(' ');
        setError(messages);
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Écran de confirmation post-inscription
  if (success) {
    return (
      <div style={{ textAlign: 'center' }}>
        <CheckCircleFilled style={{ fontSize: 48, color: '#10B981', marginBottom: 16 }} />
        <Title level={3} style={{ color: '#0F172A', marginBottom: 8 }}>
          Inscription enregistrée
        </Title>
        <Paragraph style={{ color: '#64748B', marginBottom: 24 }}>
          Un email de vérification a été envoyé à votre adresse.
          Cliquez sur le lien dans l'email pour activer votre compte.
        </Paragraph>
        <div style={{
          background: '#ECFDF5',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 24,
        }}>
          <Text style={{ color: '#064E3B', fontSize: 13 }}>
            Votre instance sera accessible sur :
          </Text>
          <br />
          <Text strong style={{ color: '#10B981', fontSize: 15 }}>
            {resultSlug}.cleoerp.com
          </Text>
        </div>
        <Link to="/login">
          <Button type="primary" size="large" block>
            Se connecter
          </Button>
        </Link>
      </div>
    );
  }

  // Indicateur slug
  const slugSuffix = () => {
    if (!slugValue || slugValue.length < 3) return null;
    if (slugStatus === 'checking') return <LoadingOutlined style={{ color: '#F59E0B' }} />;
    if (slugStatus === 'available') return <CheckCircleFilled style={{ color: '#10B981' }} />;
    if (slugStatus === 'taken') return <CloseCircleFilled style={{ color: '#EF4444' }} />;
    if (slugStatus === 'invalid') return <CloseCircleFilled style={{ color: '#F59E0B' }} />;
    return null;
  };

  return (
    <div>
      <Title level={3} style={{ color: '#0F172A', marginBottom: 4, textAlign: 'center' }}>
        Créez votre instance Cleo ERP
      </Title>
      <Paragraph style={{ color: '#64748B', textAlign: 'center', marginBottom: 24 }}>
        Essai gratuit 30 jours — carte bancaire requise
      </Paragraph>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16 }}
          onClose={() => setError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        size="large"
      >
        <Form.Item
          name="company_name"
          label="Nom de l'entreprise"
          rules={[
            { required: true, message: 'Le nom de l\'entreprise est requis' },
            { min: 2, message: 'Minimum 2 caractères' },
          ]}
        >
          <Input
            prefix={<BankOutlined style={{ color: '#94A3B8' }} />}
            placeholder="Ex : Edge SARL"
            onChange={handleCompanyNameChange}
          />
        </Form.Item>

        <Form.Item
          name="country_code"
          label="Pays"
          rules={[{ required: true, message: 'Sélectionnez votre pays' }]}
        >
          <Select
            placeholder="Sélectionnez un pays"
            suffixIcon={<GlobalOutlined style={{ color: '#94A3B8' }} />}
            options={countries.map(c => ({ value: c.code, label: c.name }))}
          />
        </Form.Item>

        <Form.Item
          label="Sous-domaine"
          help={
            slugValue && slugValue.length >= 3 ? (
              <span style={{
                color: slugStatus === 'available' ? '#10B981'
                  : slugStatus === 'taken' ? '#EF4444'
                  : slugStatus === 'invalid' ? '#F59E0B'
                  : '#64748B',
              }}>
                {slugStatus === 'checking' ? 'Vérification...' : slugMessage}
              </span>
            ) : null
          }
        >
          <Input
            prefix={<LinkOutlined style={{ color: '#94A3B8' }} />}
            suffix={slugSuffix()}
            placeholder="ex : edge-sarl"
            value={slugValue}
            onChange={handleSlugChange}
            addonAfter=".cleoerp.com"
          />
        </Form.Item>

        <Form.Item
          name="admin_email"
          label="Email administrateur"
          rules={[
            { required: true, message: 'L\'email est requis' },
            { type: 'email', message: 'Format email invalide' },
          ]}
        >
          <Input
            prefix={<MailOutlined style={{ color: '#94A3B8' }} />}
            placeholder="admin@votre-entreprise.com"
          />
        </Form.Item>

        <Form.Item
          name="admin_password"
          label="Mot de passe"
          rules={[
            { required: true, message: 'Le mot de passe est requis' },
            { min: 8, message: 'Minimum 8 caractères' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: '#94A3B8' }} />}
            placeholder="Minimum 8 caractères"
          />
        </Form.Item>

        <Form.Item
          name="confirm_password"
          label="Confirmer le mot de passe"
          dependencies={['admin_password']}
          rules={[
            { required: true, message: 'Confirmez votre mot de passe' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('admin_password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined style={{ color: '#94A3B8' }} />}
            placeholder="Identique au mot de passe"
          />
        </Form.Item>

        <Form.Item
          name="cgu"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error('Vous devez accepter les CGU')),
            },
          ]}
        >
          <Checkbox>
            <Text style={{ color: '#64748B', fontSize: 13 }}>
              J'accepte les conditions générales d'utilisation
            </Text>
          </Checkbox>
        </Form.Item>

        <Form.Item style={{ marginBottom: 12 }}>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={loading}
            disabled={slugStatus === 'taken' || slugStatus === 'invalid'}
            style={{ height: 44, fontWeight: 600 }}
          >
            Créer mon instance
          </Button>
        </Form.Item>
      </Form>

      <div style={{ textAlign: 'center' }}>
        <Text style={{ color: '#64748B', fontSize: 13 }}>
          Déjà inscrit ?{' '}
          <Link to="/login" style={{ fontWeight: 500 }}>Se connecter</Link>
        </Text>
      </div>

      <div style={{ textAlign: 'center', marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
        <Text style={{ color: '#94A3B8', fontSize: 12 }}>
          <a href="https://cleoerp.com/legal/terms/" target="_blank" rel="noopener noreferrer"
             style={{ color: '#94A3B8', marginRight: 12 }}>
            Conditions d'utilisation
          </a>
          <a href="https://cleoerp.com/legal/privacy/" target="_blank" rel="noopener noreferrer"
             style={{ color: '#94A3B8', marginRight: 12 }}>
            Confidentialité
          </a>
          <a href="https://cleoerp.com/legal/refund/" target="_blank" rel="noopener noreferrer"
             style={{ color: '#94A3B8' }}>
            Remboursement
          </a>
        </Text>
      </div>
    </div>
  );
}

export default RegisterPage;
